var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};
var _abortController, _customValidators, _host, _showErrors, _target, _nativeTarget, _targetFn, _nativeTargetFn, _validationCompleteResolver, _validationPending, _validators, _errorMessageId, _slotName, _onInvalid, _onReset, _onSlotchange, onSlotchange_fn, _setInvalidState, setInvalidState_fn, _removeInvalidState, removeInvalidState_fn, _updateValidationMessage, updateValidationMessage_fn, _getInvalidState, getInvalidState_fn, _setValidityWithOptionalTarget, setValidityWithOptionalTarget_fn;
import { html } from "lit";
import { I18nController } from "../i18n.js";
import "@shoelace-style/localize";
import "../translations/en.js";
const isNative = (target) => target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
let nextUniqueId = 0;
class ValidationController {
  constructor(host, { target, validators = [], nativeTarget }) {
    __privateAdd(this, _onSlotchange);
    __privateAdd(this, _setInvalidState);
    __privateAdd(this, _removeInvalidState);
    __privateAdd(this, _updateValidationMessage);
    __privateAdd(this, _getInvalidState);
    __privateAdd(this, _setValidityWithOptionalTarget);
    /** An internal abort controller for cancelling pending async validation. */
    __privateAdd(this, _abortController, void 0);
    /** Custom validators set at runtime. */
    __privateAdd(this, _customValidators, void 0);
    /** Controller host. */
    __privateAdd(this, _host, void 0);
    __privateAdd(this, _showErrors, void 0);
    /**
     * The element which is being validated. Either a Form Associated
     * Custom Element, or an `<input>` or `<textarea>`.
     */
    __privateAdd(this, _target, void 0);
    /**
     * The element which is being validated. Either a Form Associated
     * Custom Element, or an `<input>` or `<textarea>`.
     */
    __privateAdd(this, _nativeTarget, void 0);
    /** The target is set after the host's connectedCallback has run. */
    __privateAdd(this, _targetFn, void 0);
    /** The target is set after the host's connectedCallback has run. */
    __privateAdd(this, _nativeTargetFn, void 0);
    /** Save a reference to the validation complete resolver */
    __privateAdd(this, _validationCompleteResolver, void 0);
    __privateAdd(this, _validationPending, void 0);
    __privateAdd(this, _validators, void 0);
    __privateAdd(this, _errorMessageId, void 0);
    /** Name of slotted validation message. */
    __privateAdd(this, _slotName, void 0);
    __privateAdd(this, _onInvalid, void 0);
    __privateAdd(this, _onReset, void 0);
    __privateSet(this, _showErrors, false);
    this.validationComplete = Promise.resolve();
    __privateSet(this, _validationPending, false);
    __privateSet(this, _validators, []);
    __privateSet(this, _errorMessageId, `validation-message-error-${nextUniqueId++}`);
    __privateSet(this, _onInvalid, (event) => {
      event.stopImmediatePropagation();
      event.preventDefault();
      __privateMethod(this, _setInvalidState, setInvalidState_fn).call(this);
      if (__privateGet(this, _showErrors) !== !this.validity.valid) {
        const state = __privateMethod(this, _getInvalidState, getInvalidState_fn).call(this, this.validity);
        if (!state) {
          return;
        }
        __privateSet(this, _slotName, state.replace(/_/g, "-"));
        if (this.target.hasAttribute("aria-describedby")) {
          const currentId = this.target.getAttribute("aria-describedby");
          this.target.setAttribute("aria-describedby", `${currentId} ${__privateGet(this, _errorMessageId)}`);
        } else {
          this.target.setAttribute("aria-describedby", __privateGet(this, _errorMessageId));
        }
        __privateMethod(this, _updateValidationMessage, updateValidationMessage_fn).call(this);
        __privateSet(this, _showErrors, !this.validity.valid);
        __privateGet(this, _host).requestUpdate();
      }
    });
    __privateSet(this, _onReset, (event) => {
      const { form } = isNative(this.target) ? this.target : this.target.internals;
      if (form === event.target) {
        __privateSet(this, _showErrors, false);
        __privateMethod(this, _removeInvalidState, removeInvalidState_fn).call(this);
      }
    });
    __privateSet(this, _host, host);
    __privateGet(this, _host).addController(this);
    this.i18nController = new I18nController(host);
    if (typeof target === "function") {
      __privateSet(this, _targetFn, target);
    } else {
      __privateSet(this, _target, target || host);
    }
    if (nativeTarget && typeof nativeTarget === "function") {
      __privateSet(this, _nativeTargetFn, nativeTarget);
    }
    __privateSet(this, _validators, validators || []);
    __privateSet(this, _slotName, "");
  }
  get target() {
    if (__privateGet(this, _target)) {
      return __privateGet(this, _target);
    } else {
      throw new Error("The validation controller cannot function without a target");
    }
  }
  get nativeTarget() {
    if (__privateGet(this, _nativeTarget)) {
      return __privateGet(this, _nativeTarget);
    } else {
      throw new Error("The validation controller cannot function without a native form element");
    }
  }
  get validationMessage() {
    if (isNative(this.target)) {
      return this.target.validationMessage;
    } else {
      return this.target.internals.validationMessage;
    }
  }
  get validity() {
    if (isNative(this.target)) {
      return this.target.validity;
    } else {
      return this.target.internals.validity;
    }
  }
  async hostConnected() {
    await __privateGet(this, _host).updateComplete;
    if (!__privateGet(this, _target) && __privateGet(this, _targetFn)) {
      __privateSet(this, _target, __privateGet(this, _targetFn).call(this));
    }
    if (!__privateGet(this, _nativeTarget) && __privateGet(this, _nativeTargetFn)) {
      __privateSet(this, _nativeTarget, __privateGet(this, _nativeTargetFn).call(this));
    }
    if (isNative(this.target) && !this.target.hasAttribute("title")) {
      this.target.setAttribute("title", "");
    }
    document.addEventListener("reset", __privateGet(this, _onReset));
    if (isNative(this.target)) {
      this.target.addEventListener("invalid", __privateGet(this, _onInvalid));
    } else {
      this.nativeTarget.addEventListener("invalid", __privateGet(this, _onInvalid));
    }
  }
  hostDisconnected() {
    if (isNative(this.target)) {
      this.target.removeEventListener("invalid", __privateGet(this, _onInvalid));
    } else {
      this.nativeTarget.removeEventListener("invalid", __privateGet(this, _onInvalid));
    }
    document.removeEventListener("reset", __privateGet(this, _onReset));
  }
  hostUpdated() {
    if ("validators" in __privateGet(this, _host)) {
      __privateSet(this, _customValidators, __privateGet(this, _host).validators);
    }
  }
  render() {
    if (!__privateGet(this, _target) || !this.target || !__privateGet(this, _host)) {
      return;
    }
    const state = __privateMethod(this, _getInvalidState, getInvalidState_fn).call(this, this.validity);
    if (__privateGet(this, _showErrors) && !!state) {
      __privateSet(this, _slotName, state.replace(/_/g, "-"));
      __privateMethod(this, _setInvalidState, setInvalidState_fn).call(this);
      __privateMethod(this, _updateValidationMessage, updateValidationMessage_fn).call(this);
      return html`<slot .name=${__privateGet(this, _slotName)} part="error" @slotchange=${__privateMethod(this, _onSlotchange, onSlotchange_fn)}></slot>`;
    } else {
      __privateMethod(this, _removeInvalidState, removeInvalidState_fn).call(this);
    }
  }
  removeValidationMessage() {
    var _a;
    (_a = __privateGet(this, _host).querySelector('[part="error"]')) == null ? void 0 : _a.remove();
    if (this.target.hasAttribute("aria-describedby")) {
      const describedBy = this.target.getAttribute("aria-describedby"), ids = describedBy == null ? void 0 : describedBy.split(" "), index = ids == null ? void 0 : ids.indexOf(__privateGet(this, _errorMessageId));
      if (index !== -1 && ids && this.target && (ids == null ? void 0 : ids.length) > 1) {
        if (index) {
          ids.splice(index, 1);
        }
        const newAriaDescribedby = ids == null ? void 0 : ids.join(" ");
        if (newAriaDescribedby) {
          this.target.setAttribute("aria-describedby", newAriaDescribedby.toString());
        }
      }
    }
  }
  setCustomValidity(message) {
    if (isNative(this.target)) {
      this.target.setCustomValidity(message);
    } else {
      this.target.internals.setValidity({ customError: true }, message);
    }
  }
  validate(value) {
    const validators = [...__privateGet(this, _validators), ...__privateGet(this, _customValidators) || []], hasAsyncValidators = validators.some(({ isValid }) => isValid instanceof Promise), asyncValidators = [], validity = {};
    if (!__privateGet(this, _validationPending)) {
      this.validationComplete = new Promise((resolve) => {
        __privateSet(this, _validationCompleteResolver, resolve);
      });
      __privateSet(this, _validationPending, true);
    }
    if (__privateGet(this, _abortController)) {
      __privateGet(this, _abortController).abort();
      this.previousAbortController = __privateGet(this, _abortController);
    }
    const abortController = __privateSet(this, _abortController, new AbortController());
    if (!validators.length) {
      return;
    }
    let keyChanged = false, validationMessage = void 0;
    validators.forEach((validator) => {
      const key = validator.key || "customError", isValid = validator.isValid(this.target, value, abortController.signal), isAsyncValidator = isValid instanceof Promise;
      if (isAsyncValidator) {
        asyncValidators.push(isValid);
        isValid.then((valid) => {
          if (valid === void 0 || valid === null) {
            return;
          }
          validity[key] = !valid;
          const currentVal = value ? value.length : "0";
          if (validator.key === "rangeOverflow") {
            validationMessage = this.i18nController.term(validator.key, __privateGet(this, _host).maxLength, currentVal);
          } else if (validator.key === "rangeUnderflow") {
            validationMessage = this.i18nController.term(validator.key, __privateGet(this, _host).minLength, currentVal);
          } else {
            validationMessage = this.i18nController.term(validator.key);
          }
          __privateMethod(this, _setValidityWithOptionalTarget, setValidityWithOptionalTarget_fn).call(this, validity, validationMessage);
        });
      } else {
        validity[key] = !isValid;
        if (this.validity[key] !== !isValid) {
          keyChanged = true;
        }
        if (!isValid) {
          const currentVal = value ? value.length : "0";
          if (validator.key === "rangeOverflow") {
            validationMessage = this.i18nController.term(validator.key, __privateGet(this, _host).maxLength, currentVal);
          } else if (validator.key === "rangeUnderflow") {
            validationMessage = this.i18nController.term(validator.key, __privateGet(this, _host).minLength, currentVal);
          } else {
            validationMessage = this.i18nController.term(validator.key);
          }
        }
      }
    });
    Promise.allSettled(asyncValidators).then(() => {
      var _a;
      if (!(abortController == null ? void 0 : abortController.signal.aborted)) {
        __privateSet(this, _validationPending, false);
        (_a = __privateGet(this, _validationCompleteResolver)) == null ? void 0 : _a.call(this);
      }
    });
    if (keyChanged || !hasAsyncValidators) {
      __privateMethod(this, _setValidityWithOptionalTarget, setValidityWithOptionalTarget_fn).call(this, validity, validationMessage);
    }
  }
}
_abortController = new WeakMap();
_customValidators = new WeakMap();
_host = new WeakMap();
_showErrors = new WeakMap();
_target = new WeakMap();
_nativeTarget = new WeakMap();
_targetFn = new WeakMap();
_nativeTargetFn = new WeakMap();
_validationCompleteResolver = new WeakMap();
_validationPending = new WeakMap();
_validators = new WeakMap();
_errorMessageId = new WeakMap();
_slotName = new WeakMap();
_onInvalid = new WeakMap();
_onReset = new WeakMap();
_onSlotchange = new WeakSet();
onSlotchange_fn = function(event) {
  const elements = event.target.assignedElements({ flatten: true });
  if (elements.length > 1) {
    const nonCustomErrors = elements.filter((element) => element.tagName === "LEAF-VALIDATION-MESSAGE");
    nonCustomErrors.forEach((error) => error.remove());
  }
};
_setInvalidState = new WeakSet();
setInvalidState_fn = function() {
  if (!__privateGet(this, _target) || !this.target || !__privateGet(this, _host)) {
    return;
  }
  __privateGet(this, _target).setAttribute("invalid", "");
  if (isNative(this.target)) {
    __privateGet(this, _host).setAttribute("invalid", "");
    this.target.ariaInvalid = "true";
    __privateGet(this, _host).requestUpdate();
  }
};
_removeInvalidState = new WeakSet();
removeInvalidState_fn = function() {
  this.removeValidationMessage();
  __privateGet(this, _host).removeAttribute("invalid");
  this.target.ariaInvalid = null;
  __privateGet(this, _host).requestUpdate();
};
_updateValidationMessage = new WeakSet();
updateValidationMessage_fn = function() {
  const errorPart = __privateGet(this, _host).querySelector('[part="error"]'), currentError = __privateGet(this, _host).querySelector("leaf-validation-message");
  if (!currentError && this.validationMessage || (currentError == null ? void 0 : currentError.slot) !== __privateGet(this, _slotName) || this.validationMessage !== (errorPart == null ? void 0 : errorPart.innerText)) {
    if (currentError) {
      currentError.remove();
    }
    if (this.target.hasAttribute("aria-describedby")) {
      const currentId = this.target.getAttribute("aria-describedby");
      if (!currentId.includes("error")) {
        this.target.setAttribute("aria-describedby", `${currentId} ${__privateGet(this, _errorMessageId)}`);
      }
    } else {
      this.target.setAttribute("aria-describedby", __privateGet(this, _errorMessageId));
    }
    const formLabel = `<leaf-validation-message variant="error" slot="${__privateGet(this, _slotName)}" part="error">
      <span id=${__privateGet(this, _errorMessageId)} aria-live="assertive">${this.validationMessage}</span>
      </leaf-validation-message>`;
    __privateGet(this, _host).insertAdjacentHTML("beforeend", formLabel);
  }
};
_getInvalidState = new WeakSet();
getInvalidState_fn = function(validity) {
  if (validity.badInput) {
    return "badInput";
  } else if (validity.patternMismatch) {
    return "patternMismatch";
  } else if (validity.rangeOverflow) {
    return "rangeOverflow";
  } else if (validity.rangeUnderflow) {
    return "rangeUnderflow";
  } else if (validity.stepMismatch) {
    return "stepMismatch";
  } else if (validity.tooLong) {
    return "tooLong";
  } else if (validity.tooShort) {
    return "tooShort";
  } else if (validity.typeMismatch) {
    return "typeMismatch";
  } else if (validity.valueMissing) {
    return "valueMissing";
  }
};
_setValidityWithOptionalTarget = new WeakSet();
setValidityWithOptionalTarget_fn = function(validity, validationMessage) {
  if (isNative(this.target)) {
    this.target.setCustomValidity(validationMessage ?? "");
  } else {
    this.target.internals.setValidity(validity, validationMessage);
  }
  __privateGet(this, _host).requestUpdate();
};
export {
  ValidationController
};
//# sourceMappingURL=validation.js.map
